import React, { FC, useState, useEffect, useMemo, useRef } from 'react';
import styles from './index.module.scss';
import { DatePicker, EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { Spin } from 'antd';
import { IndexTimeActive } from '../../../SwitchDate';
import imgDevice from '../../../../imgs/img_device.png';
import imgDeviceNumber from '../../../../imgs/icon_device_number.png';
import iconVocs from '../../../../imgs/icon-VOCS.png';
import { keepOneAndWanOrLine, keepTwoOrLine } from '@/utils/utils';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { apiV2PaintingDetailVocsPost } from '@maxtropy/device-customer-apis-v2';

interface VOCsDetailModalProps {
  visible: boolean;
  defaultActiveKey: IndexTimeActive;
  defaultSelectedDate: Dayjs;
}

const ELE_COLOR = '#16DD8E';
const GAS_COLOR = '#FFCB47';

const createDualTrendOption = (data: { date: string; electricity?: number; gas?: number }[], theme: string) => {
  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis' as const,
      confine: true,
      extraCssText: 'width: 220px;',
      formatter: (items: any) => {
        const { axisValue } = items[0];
        let str = `<div style="padding: 4px;">${axisValue}</div>`;
        items.forEach((item: any) => {
          const unit = item.seriesName === '用电量' ? 'kWh' : 'm³';
          str += `<div style="margin-top:8px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${
            item.marker
          }</span>${item.seriesName}</div><div>${keepOneAndWanOrLine(item.value)} ${unit}</div></div></div>`;
        });
        return str;
      },
    },
    legend: {
      data: ['用电量', '天然气用量'],
      right: 0,
      top: 0,
      itemWidth: 16,
      itemHeight: 3,
    },
    grid: {
      bottom: 5,
    },
    xAxis: {
      type: 'category' as const,
      data: data.map(d => d.date),
    },
    yAxis: [
      {
        type: 'value' as const,
        name: 'kWh',
      },
      {
        type: 'value' as const,
        name: 'm³',
      },
    ],
    series: [
      {
        name: '用电量',
        type: 'line' as const,
        data: data.map(d => d.electricity),
        smooth: true,
        symbol: 'none',
        lineStyle: { color: ELE_COLOR, width: 2 },
        areaStyle: {
          color: {
            type: 'linear' as const,
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: ELE_COLOR + '40' },
              { offset: 1, color: ELE_COLOR + '05' },
            ],
          },
        },
      },
      {
        name: '天然气用量',
        type: 'line' as const,
        yAxisIndex: 1,
        data: data.map(d => d.gas),
        smooth: true,
        symbol: 'none',
        lineStyle: { color: GAS_COLOR, width: 2 },
        areaStyle: {
          color: {
            type: 'linear' as const,
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: GAS_COLOR + '40' },
              { offset: 1, color: GAS_COLOR + '05' },
            ],
          },
        },
      },
    ],
  };
};

const VOCsDetailModal: FC<VOCsDetailModalProps> = ({ visible, defaultActiveKey, defaultSelectedDate }) => {
  const theme = useTheme();

  // 弹窗内部独立的日期状态
  const [selectedDate, setSelectedDate] = useState<Dayjs>(defaultSelectedDate);

  // 记录上一次请求的日期，用于判断是否需要重新请求
  const lastRequestDateRef = useRef<string>('');

  // 弹窗打开时同步外部日期
  useEffect(() => {
    if (visible) {
      setSelectedDate(defaultSelectedDate);
      lastRequestDateRef.current = '';
    }
  }, [visible, defaultSelectedDate]);

  // 请求参数
  const getRequestParams = (date: Dayjs) => {
    const unit = defaultActiveKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';
    const startTime = date.startOf(unit).valueOf();
    const now = dayjs();
    const isCurrentPeriod =
      date.format(defaultActiveKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY') ===
      now.format(defaultActiveKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY');
    const endTime = isCurrentPeriod ? now.subtract(1, 'day').endOf('day').valueOf() : date.endOf(unit).valueOf();
    return { startTime, endTime };
  };

  // 弹窗打开时请求接口
  const { data, loading, run } = useRequest(
    (params: { startTime: number; endTime: number }) =>
      apiV2PaintingDetailVocsPost(params).then(res => {
        const phases = res?.workshops?.map(w => ({
          name: `涂装${w.workshop}期`,
          electricityUsage: keepOneAndWanOrLine(w.elecUsage),
          ratio: keepTwoOrLine(w.elecVocsRatio),
          phaseRatio: keepTwoOrLine(w.elecWorkshopRatio),
          gasUsage: keepOneAndWanOrLine(w.gasUsage),
          gasRatio: keepTwoOrLine(w.gasRatio),
          trendData:
            w.trend?.map(t => ({
              date: dayjs(t.timestamp).format('MM-DD'),
              electricity: t.electricity,
              gas: t.gas,
            })) || [],
        }));
        return {
          summary: res?.summary,
          phases,
        };
      }),
    {
      manual: true,
    }
  );

  // 发起请求的函数，带防重复检查
  const fetchData = (date: Dayjs) => {
    const dateKey = date.format('YYYY-MM');
    if (lastRequestDateRef.current !== dateKey) {
      lastRequestDateRef.current = dateKey;
      run(getRequestParams(date));
    }
  };

  // 弹窗打开时触发请求
  useEffect(() => {
    if (visible) {
      fetchData(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const totalElectricity = keepOneAndWanOrLine(data?.summary?.totalElecUsage);
  const totalGas = keepOneAndWanOrLine(data?.summary?.totalGasUsage);
  const electricityRatio = keepTwoOrLine(data?.summary?.elecRatio);
  const totalCount = data?.summary?.deviceCount ?? 0;

  const phases = data?.phases || [];

  const chartOptions = useMemo(() => {
    return phases.map(phase => createDualTrendOption(phase.trendData, theme));
  }, [phases, theme]);

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loading_wrapper}>
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Date Selector */}
      <div className={styles.dateSelector}>
        <DatePicker
          picker={defaultActiveKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year'}
          value={selectedDate}
          onChange={date => {
            if (date) {
              setSelectedDate(date);
              fetchData(date);
            }
          }}
          allowClear={false}
          className={styles.datePicker}
        />
      </div>

      {/* Stats Row */}
      <div className={styles.statsRow}>
        {/* Left: VOCs + Electricity */}
        <div className={styles.statsBoxLeft}>
          <div className={styles.deviceInfo}>
            <img className={styles.statsDeviceIcon} src={iconVocs} alt="VOCS" />
            <span className={styles.statsDeviceName}>VOCS</span>
          </div>
          <div className={styles.statsElectricity}>
            <span className={styles.statsLabel}>总用电量(kWh):</span>
            <span className={styles.statsValue}>{totalElectricity}</span>
          </div>
          <div className={styles.statsRatio}>
            <span className={styles.statsLabel}>占涂装总用电量:</span>
            <div className={styles.ratioBar}>
              <div className={styles.ratioBarFill} style={{ width: `${Number(electricityRatio) || 0}%` }} />
            </div>
            <span className={styles.ratioText}>{electricityRatio}%</span>
          </div>
        </div>
        {/* Middle: Gas */}
        <div className={styles.statsBoxGas}>
          <span className={styles.statsLabel}>总天然气用量(m³):</span>
          <span className={styles.statsGasValue}>{totalGas}</span>
        </div>
        {/* Right: Device Count */}
        <div className={styles.statsBoxRight}>
          <img className={styles.statsCountIcon} src={imgDeviceNumber} alt="设备数量" />
          <span className={styles.statsLabel}>设备总数:</span>
          <span className={styles.statsCountValue}>{totalCount}台</span>
        </div>
      </div>

      {/* Phase Cards */}
      {phases.length > 0 ? (
        <div className={styles.phaseCards}>
          {phases.map((phase, index) => (
            <div key={phase.name} className={styles.phaseCard}>
              <div className={styles.phaseHeader}>
                <span className={styles.phaseName}>{phase.name}</span>
              </div>
              <div className={styles.phaseContent}>
                <div className={styles.phaseRightCol}>
                  {/* Top Box: Electricity */}
                  <div className={styles.phaseBox}>
                    <div className={styles.phaseMetric}>
                      <span className={styles.phaseMetricLabel}>用电量(kWh):</span>
                      <span className={styles.phaseMetricValue}>{phase.electricityUsage}</span>
                    </div>
                    <div className={styles.phaseBoxInnerDivider} />
                    <div className={styles.phaseMetric}>
                      <span className={styles.phaseMetricLabel}>占比:</span>
                      <div className={styles.ratioBar}>
                        <div className={styles.ratioBarFill} style={{ width: `${Number(phase.ratio) || 0}%` }} />
                      </div>
                      <span className={styles.ratioText}>{phase.ratio}%</span>
                    </div>
                    <div className={styles.phaseMetric}>
                      <span className={styles.phaseMetricLabel}>占{phase.name}总用电量:</span>
                      <div className={styles.ratioBar}>
                        <div className={styles.ratioBarFill} style={{ width: `${Number(phase.phaseRatio) || 0}%` }} />
                      </div>
                      <span className={styles.ratioText}>{phase.phaseRatio}%</span>
                    </div>
                  </div>
                  {/* Bottom Box: Gas */}
                  <div className={styles.phaseBox}>
                    <div className={styles.phaseMetric}>
                      <span className={styles.phaseMetricLabel}>天然气用量(m³):</span>
                      <span className={styles.phaseGasValue}>{phase.gasUsage}</span>
                    </div>
                    <div className={styles.phaseMetric}>
                      <span className={styles.phaseMetricLabel}>占比:</span>
                      <div className={styles.ratioBarGas}>
                        <div className={styles.ratioBarGasFill} style={{ width: `${Number(phase.gasRatio) || 0}%` }} />
                      </div>
                      <span className={styles.ratioText}>{phase.gasRatio}%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.phaseChart}>
                <div className={styles.phaseChartTitle}>用能趋势</div>
                <div className={styles.phaseChartWrapper}>
                  <EchartsWithTheme
                    theme={theme}
                    type="line"
                    style={{ width: '100%', height: '100%' }}
                    option={chartOptions[index]}
                    empty={{ isShowEmpty: !phase.trendData?.length, emptyNode: <Empty /> }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty />
      )}
    </div>
  );
};

export default VOCsDetailModal;
